/**
 * SimpleReach - Message-First Outreach Modal
 *
 * Renders via portal to document.body (escapes parent stacking contexts).
 * Send buttons are native <a href="mailto:"> for guaranteed browser support.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  X, Loader2, Linkedin, Copy, Check,
  ChevronDown, Sparkles, RefreshCw, Edit3,
  User, ArrowRight, MessageCircle, Briefcase, Target
} from 'lucide-react';
import api from '../../utils/axios';
import { useToast } from '../shared/Toast';

const QUICK_TONES = [
  { id: 'conversational', label: 'Warm', icon: MessageCircle },
  { id: 'professional', label: 'Polished', icon: Briefcase },
  { id: 'authoritative', label: 'Direct', icon: Target },
];

const TONES = [
  { id: 'conversational', label: 'Casual' },
  { id: 'professional', label: 'Professional' },
  { id: 'authoritative', label: 'Bold' },
];

export default function SimpleReach({
  isOpen,
  onClose,
  leadId,
  contact,
  onSendComplete,
  initialIntent = 'intro',
}) {
  const toast = useToast();

  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedMessage, setEditedMessage] = useState('');

  const [selectedTone, setSelectedTone] = useState('professional');
  const [showToneOptions, setShowToneOptions] = useState(false);

  const [copied, setCopied] = useState(false);
  const [timeSaved, setTimeSaved] = useState(0);

  const [brandVoice, setBrandVoice] = useState(null);
  const [brandVoiceChecked, setBrandVoiceChecked] = useState(false);
  const brandVoiceFetched = useRef(false);

  const [showVoiceSetup, setShowVoiceSetup] = useState(false);
  const [setupSignature, setSetupSignature] = useState('');
  const [setupTone, setSetupTone] = useState('professional');
  const [isSavingVoice, setIsSavingVoice] = useState(false);

  const textareaRef = useRef(null);

  const contactName = contact?.name || contact?.fullName ||
    `${contact?.firstName || contact?.first_name || ''} ${contact?.lastName || contact?.last_name || ''}`.trim() || 'there';
  const firstName = contact?.firstName || contact?.first_name || contactName.split(' ')[0] || 'there';
  const cleanEmail = (raw) => {
    if (!raw || typeof raw !== 'string') return '';
    const cleaned = raw.replace(/[{}"[\]]/g, '').trim();
    if (!cleaned.includes('@')) return '';
    const parts = cleaned.split(/[,;]\s*/);
    const first = parts.find(p => p.trim().includes('@'));
    return first ? first.trim() : '';
  };

  const contactEmail = cleanEmail(contact?.email || contact?.workEmail || '');
  const contactLinkedIn = contact?.linkedin_url || contact?.linkedinUrl || contact?.linkedin || '';
  const contactTitle = contact?.title || contact?.jobTitle || contact?.job_title || '';
  const contactCompany = contact?.companyName || contact?.company_name || contact?.company || '';

  // Resolved email — may be fetched from backend if not in initial props
  const [resolvedEmail, setResolvedEmail] = useState(contactEmail);

  const currentBody = isEditing ? editedMessage : message;
  const gmailComposeUrl = resolvedEmail
    ? `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(resolvedEmail)}&su=${encodeURIComponent(subject)}&body=${encodeURIComponent(currentBody)}`
    : null;
  const outlookComposeUrl = resolvedEmail
    ? `https://outlook.office.com/mail/deeplink/compose?to=${encodeURIComponent(resolvedEmail)}&subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(currentBody)}`
    : null;

  // ── Lifecycle ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (contactEmail) setResolvedEmail(contactEmail);
  }, [contactEmail]);

  // If no email in props, try fetching from backend (reveal may have persisted it)
  useEffect(() => {
    if (isOpen && !resolvedEmail && leadId) {
      api.post('/api/person-enrich/reveal', {
        fullName: contactName,
        companyName: contactCompany,
        linkedInUrl: contactLinkedIn || undefined,
      }).then(res => {
        const found = cleanEmail(res.data?.email || res.data?.emails?.[0]?.value || '');
        if (found) setResolvedEmail(found);
      }).catch(() => {});
    }
  }, [isOpen, resolvedEmail, leadId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!brandVoiceFetched.current && isOpen) {
      brandVoiceFetched.current = true;
      fetchBrandVoice();
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && contact && !message && brandVoiceChecked && !showVoiceSetup) {
      generateMessage();
    }
  }, [isOpen, contact, brandVoiceChecked, showVoiceSetup]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isOpen) {
      setMessage('');
      setSubject('');
      setEditedMessage('');
      setIsEditing(false);
      setCopied(false);
      setShowToneOptions(false);
    }
  }, [isOpen]);

  // ── Brand voice ────────────────────────────────────────────────────────

  const fetchBrandVoice = async () => {
    try {
      const res = await api.get('/api/ai/brand-voice');
      if (res.data?.success && res.data.brandVoice) {
        setBrandVoice(res.data.brandVoice);
      } else {
        setShowVoiceSetup(true);
      }
    } catch (err) {
      console.warn('Could not fetch brand voice:', err);
    } finally {
      setBrandVoiceChecked(true);
    }
  };

  const saveInlineBrandVoice = async () => {
    if (!setupSignature.trim()) return;
    setIsSavingVoice(true);
    try {
      const newBrandVoice = {
        name: 'My Voice', tone: setupTone, signature: setupSignature.trim(),
        company_name: '', custom_instructions: '', value_props: [], sample_messages: [],
      };
      await api.put('/api/ai/brand-voice', { brandVoice: newBrandVoice });
      setBrandVoice(newBrandVoice);
      setShowVoiceSetup(false);
      generateMessage();
    } catch (err) {
      console.error('Failed to save brand voice:', err);
      setShowVoiceSetup(false);
      generateMessage();
    } finally {
      setIsSavingVoice(false);
    }
  };

  // ── Message generation ─────────────────────────────────────────────────

  const generateMessage = useCallback(async () => {
    setIsGenerating(true);
    try {
      const response = await api.post('/api/ai/generate-outreach', {
        contact: {
          name: contactName, title: contactTitle, company: contactCompany,
          industry: contact?.industry, location: contact?.location || contact?.city,
        },
        intent: initialIntent, outreachContext: 'sales',
        brandVoice, generateMultiple: false, leadId: leadId || undefined,
      });

      let generatedText = '';
      if (response.data?.message) {
        generatedText = typeof response.data.message === 'string'
          ? response.data.message : response.data.message?.text || '';
      } else if (response.data?.variants?.[0]) {
        const variant = response.data.variants[0];
        generatedText = typeof variant === 'string' ? variant : variant?.text || '';
      } else if (response.data?.text) {
        generatedText = response.data.text;
      }

      if (generatedText.startsWith('{') || generatedText.startsWith('[')) {
        try {
          const parsed = JSON.parse(generatedText);
          generatedText = parsed.text || parsed.body || parsed.message || generatedText;
        } catch { /* keep as-is */ }
      }

      const subjectMatch = generatedText.match(/^Subject:\s*(.+?)(?:\n|$)/im);
      let subjectLine, bodyText;
      if (subjectMatch) {
        subjectLine = subjectMatch[1].trim();
        bodyText = generatedText.replace(/^Subject:\s*.+?(?:\n|$)/im, '').trim();
      } else {
        subjectLine = `Quick note - ${firstName}`;
        bodyText = generatedText.trim();
      }

      setSubject(subjectLine);
      setMessage(bodyText);
      setEditedMessage(bodyText);
      setTimeSaved(15);
    } catch (error) {
      console.error('Failed to generate message:', error);
      const fallback = `Hi ${firstName},\n\nI noticed ${contactCompany ? `${contactCompany} is doing great work` : 'your profile'} and wanted to connect.\n\nWould love to chat briefly about how we might be able to help.\n\nBest,\n[Your name]`;
      setMessage(fallback);
      setEditedMessage(fallback);
      setSubject(`Quick connect - ${firstName}`);
    } finally {
      setIsGenerating(false);
    }
  }, [contactName, contactTitle, contactCompany, contact, initialIntent, brandVoice, leadId, firstName]);

  const handleRegenerate = () => {
    setMessage('');
    setEditedMessage('');
    generateMessage();
  };

  // ── Send tracking ──────────────────────────────────────────────────────

  const handleMailtoClick = () => {
    // Defer so the browser follows the mailto: href before React re-renders
    setTimeout(() => {
      trackOutreach('email');
      toast.success(`Email ready to send to ${firstName}`);
      onClose();
    }, 300);
  };

  const handleCopy = async () => {
    const textToCopy = isEditing ? editedMessage : message;
    await navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    trackOutreach('copy');
  };

  const handleLinkedIn = async () => {
    const textToSend = isEditing ? editedMessage : message;
    await navigator.clipboard.writeText(textToSend.substring(0, 300));
    if (contactLinkedIn) window.open(contactLinkedIn, '_blank');
    trackOutreach('linkedin');
    toast.success('Message copied — LinkedIn opened');
    onClose();
  };

  const trackOutreach = async (channel) => {
    try {
      if (leadId) await api.post(`/api/leads/${leadId}/deploy`, { channel });
      const stats = JSON.parse(localStorage.getItem('eq_outreach_stats') || '{}');
      stats.totalSent = (stats.totalSent || 0) + 1;
      stats.timeSaved = (stats.timeSaved || 0) + timeSaved;
      stats[`${channel}Count`] = (stats[`${channel}Count`] || 0) + 1;
      localStorage.setItem('eq_outreach_stats', JSON.stringify(stats));
    } catch (err) {
      console.warn('Could not track outreach:', err);
    }
  };

  const handleClose = () => {
    onClose();
  };

  // ── Render via portal ──────────────────────────────────────────────────

  if (!isOpen) return null;

  const isLoading = !brandVoiceChecked || (isGenerating && !message);
  const isReady = !isLoading && !showVoiceSetup && !!message;

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      {/* Loading */}
      {isLoading && (
        <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-8">
          <div className="flex flex-col items-center justify-center py-8">
            <div className="relative mb-4">
              <Loader2 className="w-10 h-10 text-[#5a20ff] animate-spin" />
              {brandVoice && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-[#5a20ff] to-[#52dcb3] rounded-full flex items-center justify-center">
                  <Sparkles className="w-2.5 h-2.5 text-white" />
                </div>
              )}
            </div>
            <p className="text-lg font-semibold text-slate-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              Writing to {firstName}
            </p>
            <p className="text-sm text-slate-500 mt-1">
              {brandVoice ? 'Applying your voice' : 'Personalizing'}
            </p>
          </div>
        </div>
      )}

      {/* Voice setup */}
      {!isLoading && showVoiceSetup && (
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-[#5a20ff] to-[#52dcb3] rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "'Playfair Display', serif" }}>Set Your Voice</h2>
                <p className="text-xs text-slate-500">Personalize your outreach</p>
              </div>
            </div>
          </div>
          <div className="px-6 py-5 space-y-5">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Your signature</label>
              <textarea value={setupSignature} onChange={(e) => setSetupSignature(e.target.value)}
                placeholder={`Best,\nYour Name\nTitle`}
                className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#5a20ff] focus:ring-1 focus:ring-[#5a20ff]/20 resize-none"
                rows={3} autoFocus />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Your style</label>
              <div className="grid grid-cols-3 gap-2">
                {QUICK_TONES.map((tone) => {
                  const Icon = tone.icon;
                  return (
                    <button key={tone.id} onClick={() => setSetupTone(tone.id)}
                      className={`p-3 rounded-xl border-2 text-center transition-all ${setupTone === tone.id ? 'border-[#5a20ff] bg-[#5a20ff]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                      <Icon className={`w-5 h-5 mx-auto mb-1.5 ${setupTone === tone.id ? 'text-[#5a20ff]' : 'text-slate-400'}`} />
                      <span className={`text-xs font-medium ${setupTone === tone.id ? 'text-[#5a20ff]' : 'text-slate-700'}`}>{tone.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="px-6 py-4 bg-slate-50 border-t border-slate-100">
            <button onClick={saveInlineBrandVoice} disabled={!setupSignature.trim() || isSavingVoice}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-[#5a20ff] to-[#52dcb3] text-white font-semibold rounded-xl hover:opacity-90 disabled:opacity-50 transition-all">
              {isSavingVoice ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <>Continue <ArrowRight className="w-4 h-4" /></>}
            </button>
            <button onClick={() => { setShowVoiceSetup(false); generateMessage(); }}
              className="w-full mt-2 text-xs text-slate-400 hover:text-slate-600 transition-colors">Skip for now</button>
          </div>
        </div>
      )}

      {/* Main message */}
      {isReady && (
        <div className="w-full max-w-lg bg-white rounded-2xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 flex-shrink-0">
            <div>
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "'Playfair Display', serif" }}>Reach {firstName}</h2>
              {contactTitle && contactCompany && (
                <p className="text-xs text-slate-500 mt-0.5">{contactTitle} at {contactCompany}</p>
              )}
            </div>
            <button onClick={handleClose} className="p-2 hover:bg-slate-100 rounded-full transition-colors">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-4">
            <div className="mb-3">
              <label className="text-xs font-medium text-slate-500 mb-1 block">Subject</label>
              <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-[#5a20ff] focus:ring-1 focus:ring-[#5a20ff]/20" />
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-slate-500">Message</label>
                <button onClick={() => { setIsEditing(!isEditing); if (!isEditing) setTimeout(() => textareaRef.current?.focus(), 0); }}
                  className="flex items-center gap-1 text-xs text-[#5a20ff] hover:underline">
                  <Edit3 className="w-3 h-3" /> {isEditing ? 'Done' : 'Edit'}
                </button>
              </div>
              {isEditing ? (
                <textarea ref={textareaRef} value={editedMessage} onChange={(e) => setEditedMessage(e.target.value)}
                  className="w-full px-3 py-3 text-sm border-2 border-[#5a20ff] rounded-xl focus:outline-none resize-none leading-relaxed" rows={8} />
              ) : (
                <div className="bg-slate-50 rounded-xl p-4 border border-slate-100">
                  <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed">{message}</pre>
                </div>
              )}
            </div>

            <button onClick={handleRegenerate} disabled={isGenerating}
              className="flex items-center gap-2 text-sm text-slate-500 hover:text-[#5a20ff] transition-colors mb-4">
              <RefreshCw className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} /> Regenerate
            </button>

            <div className="mb-4">
              <button onClick={() => setShowToneOptions(!showToneOptions)}
                className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 transition-colors">
                <ChevronDown className={`w-3 h-3 transition-transform ${showToneOptions ? 'rotate-180' : ''}`} /> Adjust tone
              </button>
              {showToneOptions && (
                <div className="flex gap-2 mt-2">
                  {TONES.map((tone) => (
                    <button key={tone.id} onClick={() => { setSelectedTone(tone.id); handleRegenerate(); }}
                      className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all ${selectedTone === tone.id ? 'bg-[#5a20ff] text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                      {tone.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Footer — Gmail / Outlook web compose */}
          <div className="px-5 py-4 border-t border-slate-100 bg-slate-50/50 flex-shrink-0">
            {resolvedEmail ? (
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                <a href={gmailComposeUrl} target="_blank" rel="noopener noreferrer" onClick={handleMailtoClick}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-800 font-semibold rounded-xl hover:border-red-300 hover:bg-red-50 transition-all no-underline">
                  <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                    <path d="M22 6L12 13L2 6" stroke="#EA4335" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <rect x="2" y="4" width="20" height="16" rx="2" stroke="#4285F4" strokeWidth="2"/>
                  </svg>
                  Send with Gmail
                </a>
                <a href={outlookComposeUrl} target="_blank" rel="noopener noreferrer" onClick={handleMailtoClick}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-white border border-slate-200 text-slate-800 font-semibold rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-all no-underline">
                  <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none">
                    <rect x="2" y="4" width="20" height="16" rx="2" stroke="#0078D4" strokeWidth="2"/>
                    <path d="M2 6L12 13L22 6" stroke="#0078D4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Send with Outlook
                </a>
              </div>
            ) : (
              <p className="text-sm text-center text-slate-400 py-2">No email address available</p>
            )}

            <div className="flex items-center justify-center gap-2 mt-2">
              <button onClick={handleCopy}
                className="flex items-center gap-1 px-2 py-1 text-xs text-slate-400 hover:text-[#5a20ff] hover:bg-[#5a20ff]/5 rounded-lg transition-colors">
                {copied ? <><Check className="w-3 h-3 text-emerald-500" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
              </button>
              {contactLinkedIn && (
                <button onClick={handleLinkedIn}
                  className="flex items-center gap-1 px-2 py-1 text-xs text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors">
                  <Linkedin className="w-3 h-3" /> LinkedIn
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
}
